<script setup lang="ts">
import { ref, computed, watch, useTemplateRef } from 'vue';
import { z } from 'zod';
import { UiForm, UiFormField, UiInputField, UiText, UiLink, useApi, useToaster } from '@shopexperts/shared';
import type { RegisterResponse } from '@shopexperts/shared';
import { useTokenStorage } from '@/app/composables/useTokenStorage';

const emit = defineEmits<{
    (e: 'authenticated', data: RegisterAuthResult): void;
    (e: 'switch-to-login', email: string): void;
}>();

export interface RegisterAuthResult {
    user: RegisterResponse['user'];
    token: string;
    type: 'client';
    companyName: string;
}

interface Props {
    email?: string;
    storeName?: string;
    storeUrl?: string;
}

const props = withDefaults(defineProps<Props>(), {
    storeName: '',
    storeUrl: '',
});

const api = useApi();
const toaster = useToaster();
const tokenStorage = useTokenStorage();

const isLoading = ref(false);

// Email availability check
const emailForCheck = ref<string>('');
const emailError = ref<string | undefined>(undefined);
const isCheckingEmail = ref(false);
let emailCheckTimeout: ReturnType<typeof setTimeout> | null = null;

const registerModel = ref({
    email: props.email || '',
    firstName: '',
    lastName: '',
    companyName: props.storeName || '',
    companyWebsite: props.storeUrl || '',
    password: '',
    confirmPassword: '',
});

watch(() => props.email, email => {
    if (email) registerModel.value.email = email;
});

const initialValues = { ...registerModel.value };

// Basic email validation
function isValidEmailFormat(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

watch([() => props.storeName, () => props.storeUrl], ([newStoreName, newStoreUrl]) => {
    registerModel.value.companyName = newStoreName || '';
    registerModel.value.companyWebsite = newStoreUrl || '';
}, { immediate: true });

// Debounced email check
watch(emailForCheck, async (newEmail) => {
    // Clear any existing timeout
    if (emailCheckTimeout) {
        clearTimeout(emailCheckTimeout);
    }

    // Clear error if email is empty or invalid format
    if (!newEmail || !isValidEmailFormat(newEmail)) {
        emailError.value = undefined;
        return;
    }

    // Debounce the email check (500ms after user stops typing)
    emailCheckTimeout = setTimeout(async () => {
        isCheckingEmail.value = true;
        try {
            const result = await api.public.auth.checkEmail({
                body: { email: newEmail },
            });
            
            if (result.exists) {
                emailError.value = 'This email is already registered.';
            } else {
                emailError.value = undefined;
            }
        } catch (error: any) {
            // If 422, email exists
            if (error?.response?.status === 422 || error?.response?.data?.exists) {
                emailError.value = error?.response?.data?.message || 'This email is already registered.';
            } else {
                // Network or unexpected error - don't show to user
                emailError.value = undefined;
            }
        } finally {
            isCheckingEmail.value = false;
        }
    }, 500);
});

function handleEmailUpdate(value: string) {
    registerModel.value.email = value;
    emailForCheck.value = value;
}

const registerSchema = z.object({
    email: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.email('Please enter a valid email address')
    ),
    firstName: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(1, 'First name is required')
            .max(100, 'First name must be 100 characters or less')
    ),
    lastName: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(1, 'Last name is required')
            .max(100, 'Last name must be 100 characters or less')
    ),
    companyName: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(1, 'Company name is required')
            .max(255, 'Company name must be 255 characters or less')
    ),
    companyWebsite: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .max(255, 'URL must be 255 characters or less')
            .optional()
            .or(z.literal(''))
    ),
    password: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(8, 'Password must be at least 8 characters')
            .max(128, 'Password must be 128 characters or less')
    ),
    confirmPassword: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string().min(1, 'Please confirm your password')
    ),
}).refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});

const serverErrors = computed(() => {
    if (emailError.value) {
        return { email: emailError.value as unknown as string };
    }
    return undefined;
});

const submitRef = useTemplateRef<HTMLButtonElement>('submitRef');

function triggerSubmit() {
    submitRef.value?.click();
}

async function onSubmit(values: {
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    companyWebsite?: string;
    password: string;
    confirmPassword: string;
}) {
    // Don't submit if there's an email error
    if (emailError.value) {
        return;
    }
    
    isLoading.value = true;
    
    try {
        const result = await api.public.auth.register({
            body: {
                email: values.email,
                password: values.password,
                first_name: values.firstName,
                last_name: values.lastName,
                company_name: values.companyName,
                company_website: values.companyWebsite || undefined,
            },
        });
        
        // Store credentials using the token storage abstraction layer
        // This handles: API header, Pinia state, localStorage, and role_id mapping
        tokenStorage.setCredentials(result.token, result.user, 'client');
        
        toaster.create({
            title: 'Account created!',
            description: 'Your account has been created successfully.',
            type: 'success',
        });
        
        emit('authenticated', {
            user: result.user,
            token: result.token,
            type: 'client',
            companyName: values.companyName,
        });
    } catch (error: any) {
        console.error('[RegisterForm] Registration error:', error);
        
        const errorMessage = 
            error?.response?.data?.message || 
            error?.response?.data?.error ||
            error?.message ||
            'Failed to create account. Please try again.';
        
        toaster.create({
            title: 'Registration failed',
            description: errorMessage,
            type: 'error',
        });
    } finally {
        isLoading.value = false;
    }
}

defineExpose({ triggerSubmit, isLoading, email: computed(() => registerModel.value.email) });
</script>

<template>
    <UiForm v-model="registerModel" :initial-values="initialValues" :schema="registerSchema" :server-errors="serverErrors" @submit="onSubmit">
        <div class="flex flex-col gap-5">
            <button ref="submitRef" type="submit" class="hidden" />

            <UiFormField name="email" label="Email" type="email">
                <template #default="{ modelValue, onValidate, id, invalid }">
                    <div class="flex flex-col gap-1">
                        <UiInputField
                            :id="id"
                            name="email"
                            type="email"
                            :model-value="modelValue as string"
                            :invalid="invalid || !!emailError"
                            :error="''"
                            :disabled="isLoading"
                            placeholder="you@example.com"
                            @update:model-value="handleEmailUpdate"
                            @validate="onValidate"
                        />
                        <div v-if="emailError" class="flex items-center gap-1 text-sm text-se-error">
                            <UiText as="span" variant="caption-normal" class="text-se-danger">{{ emailError }}</UiText>
                            <UiLink href="#" label="Sign in instead" @click.prevent="emit('switch-to-login', registerModel.email)" />
                        </div>
                    </div>
                </template>
            </UiFormField>

            <div class="grid grid-cols-2 gap-4">
                <UiFormField name="firstName" label="First Name" type="text">
                    <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                        <UiInputField
                            :id="id"
                            name="firstName"
                            :model-value="modelValue as string"
                            :invalid="invalid"
                            :error="error"
                            :disabled="isLoading"
                            placeholder="John"
                            @update:model-value="onUpdateModelValue"
                            @validate="onValidate"
                        />
                    </template>
                </UiFormField>

                <UiFormField name="lastName" label="Last Name" type="text">
                    <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                        <UiInputField
                            :id="id"
                            name="lastName"
                            :model-value="modelValue as string"
                            :invalid="invalid"
                            :error="error"
                            :disabled="isLoading"
                            placeholder="Doe"
                            @update:model-value="onUpdateModelValue"
                            @validate="onValidate"
                        />
                    </template>
                </UiFormField>
            </div>

            <UiFormField name="companyName" label="Company Name" type="text">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="companyName"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="isLoading"
                        placeholder="Your Company Inc."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="companyWebsite" label="Company Website (Optional)" type="text">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="companyWebsite"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="isLoading"
                        placeholder="yourcompany.com"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="password" label="Password" type="password">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="password"
                        type="password"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="isLoading"
                        placeholder="Create a password (min. 8 characters)"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="confirmPassword" label="Confirm Password" type="password">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="confirmPassword"
                        type="password"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="isLoading"
                        placeholder="Confirm your password"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>
        </div>
    </UiForm>
</template>
